import { PERFORMANCE_PRODUCT_LIST } from "constants/chart.constants";
import {
  FORECAST_KEY,
  MY_FOCUS_FIELDS,
  ORG_FOCUS_FIELDS,
  ORG_GEO_FIELDS,
  ORG_GEO_SIMS,
  PROJECT_FOCUS_FIELDS
} from "constants/settings.constants";
import { groupBy, sortBy } from "lodash";
import { getCumPlusForecastTitle } from "utils";
import getColumnLabelWithContext from "utils/column/getColumnLabelWithContext";

import {
  IGroupByListColumn,
  IGroupByListItem,
  withoutShapefileBoundaryColumns
} from "models";
import {
  IGroupColumn,
  IProductColumn,
  IPropertyColumn,
  IPropertyColumnMap,
  ISubgroupColumn
} from "models/columns";
import { EntityKind } from "models/entityKind";

import { getProductLabel } from "components/charts/utils/getProductLabel";
import {
  geoVolumeCategoryTitle,
  padMetricsCategoryTitle
} from "components/groupBy/hooks/useCategories";

import { getPerformanceRatioDisplayName, getProductDisplayName } from ".";

/**
 * Get the organized multilevel column property tree for a group of columns.
 *
 * @param {IGroupByListItem[]} groups the one level groups of columns
 * @returns {IGroupColumn[]} The column property tree
 */
const getColumnPropertyTree = (
  groups: IGroupByListItem[],
  entityKind: string = EntityKind.Well,
  isFacility: boolean = false
): [IGroupColumn[], IPropertyColumnMap] => {
  if (!groups) return [[], {}];

  // Column titles carry the ratio, which the stored product name leaves out.
  const productTitlePrefix = (product: string) =>
    getPerformanceRatioDisplayName(getProductLabel(product, entityKind));

  // A stale boundary column persisted by an older session would otherwise be
  // offered as a table column and come back unresolvable in a later one.
  const tree = groups
    .map((group) => ({
      ...group,
      columns: withoutShapefileBoundaryColumns(group.columns ?? [])
    }))
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((group) => {
      const productGroupColumnList: IProductColumn[] = [];
      const subgroupColumnList: ISubgroupColumn[] = [];
      if (group.name === "Forecast" || group.name === "Performance") {
        const products = groupBy(group.columns, "product");

        for (const [product, productColumns] of Object.entries(products)) {
          if (!isFacility && product.includes("CO2")) continue;

          const productSubgroupColumnList: ISubgroupColumn[] = [];

          const subgroups = groupBy(productColumns, "subgroup");
          for (const [subgroup, subgroupColumns] of Object.entries(subgroups)) {
            const columns = subgroupColumns as IGroupByListColumn[];
            const columnsList: IPropertyColumn[] = columns.reduce((list, column) => {
              if (!isFacility && column.product?.includes("CO2")) return list;

              list.push({
                id: column.id.toString(),
                hexColor: group.hexColor,
                canBin: column.canBin,
                dataType: column.dataType,
                property: column.property,
                title: `${productTitlePrefix(product)}: ${column.title}`,
                sortOrder: column.withinSubgroupOrder
              });
              if (
                group.name === "Performance" &&
                (column.property.endsWith("Cum") ||
                  (column.subgroup === "Date Range" &&
                    column.property.includes("Cumulative")))
              ) {
                list.push({
                  id: column.id.toString(),
                  hexColor: group.hexColor,
                  canBin: column.canBin,
                  dataType: column.dataType,
                  property: `${column.property}.${FORECAST_KEY}`,
                  title: `${productTitlePrefix(product)}: ${getCumPlusForecastTitle(
                    column.title,
                    "",
                    column.subgroup
                  )}`,
                  sortOrder: column.withinSubgroupOrder
                });
              }
              return list;
            }, []);
            productSubgroupColumnList.push({
              key: subgroup,
              title: subgroup,
              columns: sortBy(columnsList, ["sortOrder"])
            });
          }

          productGroupColumnList.push({
            productKey: product,
            productDisplayName: getProductDisplayName(product),
            subgroups: productSubgroupColumnList
          });
        }

        if (group.name === "Performance") {
          const performanceOrderMap = new Map<string, number>(
            PERFORMANCE_PRODUCT_LIST.map((p, i) => [p.key, i])
          );
          // Backend sends "On Time" with a space
          performanceOrderMap.set("On Time", performanceOrderMap.get("OnTime"));
          const getOrder = (key: string) =>
            performanceOrderMap.get(key ?? "") ?? Infinity;
          const withOrder = productGroupColumnList.map((item) => ({
            item,
            order: getOrder(item.productKey)
          }));
          withOrder.sort((a, b) => a.order - b.order);
          productGroupColumnList.splice(
            0,
            productGroupColumnList.length,
            ...withOrder.map((x) => x.item)
          );
        }
      } else if (
        group.name === ORG_FOCUS_FIELDS ||
        group.name === MY_FOCUS_FIELDS ||
        group.name === PROJECT_FOCUS_FIELDS ||
        group.name === ORG_GEO_FIELDS ||
        group.name === ORG_GEO_SIMS
      ) {
        const subgroups = groupBy(group.columns, "subgroup");
        for (const [subgroup, subgroupColumns] of Object.entries(subgroups)) {
          const columns = subgroupColumns as IGroupByListColumn[];
          const columnsList: IPropertyColumn[] = columns
            .map((column) => {
              if (!isFacility && column.product?.includes("CO2")) return null; // Skip CO2 columns when not in facility mode

              const title = getColumnLabelWithContext(
                column,
                false,
                null,
                false,
                ""
              ).lightContextTitle;
              if (title) {
                return {
                  id: column.id.toString(),
                  hexColor: group.hexColor,
                  canBin: column.canBin,
                  dataType: column.dataType,
                  property: column.property,
                  title: title,
                  sortOrder: column.withinSubgroupOrder
                };
              } else {
                return {
                  id: column.id.toString(),
                  hexColor: group.hexColor,
                  canBin: column.canBin,
                  dataType: column.dataType,
                  property: column.property,
                  title: column.title,
                  sortOrder: column.withinSubgroupOrder
                };
              }
            })
            .filter(Boolean); // Filter out null values

          const isGeoGroup = group.name === ORG_GEO_FIELDS || group.name === ORG_GEO_SIMS;
          subgroupColumnList.push({
            key: subgroup,
            title: subgroup,
            columns: sortBy(columnsList, ["sortOrder"]),
            ...(isGeoGroup &&
              columns.every(
                (column) => column.subgroup == null || column.subgroup === ""
              ) && {
                hasNoSubgroup: true
              })
          });
        }
      } else if (
        group.name === geoVolumeCategoryTitle ||
        group.name === padMetricsCategoryTitle
      ) {
        const subgroups = groupBy(group.columns, "subgroup");
        for (const [subgroup, subgroupColumns] of Object.entries(subgroups)) {
          const columns = subgroupColumns as IGroupByListColumn[];
          const columnsList: IPropertyColumn[] = columns
            .map((column) => {
              if (!isFacility && column.product?.includes("CO2")) return null;

              const contextTitle = getColumnLabelWithContext(
                column,
                false,
                null,
                false,
                ""
              ).fullContextTitle;

              return {
                id: column.id.toString(),
                hexColor: group.hexColor,
                canBin: column.canBin,
                dataType: column.dataType,
                property: column.property,
                title: contextTitle || column.title,
                sortOrder: column.withinSubgroupOrder
              };
            })
            .filter(Boolean);

          subgroupColumnList.push({
            key: subgroup,
            title: subgroup,
            columns: sortBy(columnsList, ["sortOrder"])
          });
        }
      } else {
        const subgroups = groupBy(group.columns, "subgroup");
        for (const [subgroup, subgroupColumns] of Object.entries(subgroups)) {
          const columns = subgroupColumns as IGroupByListColumn[];
          const columnsList: IPropertyColumn[] = columns
            .map((column) => {
              if (!isFacility && column.product?.includes("CO2")) return null; // Skip CO2 columns when not in facility mode

              return {
                id: column.id.toString(),
                hexColor: group.hexColor,
                canBin: column.canBin,
                dataType: column.dataType,
                property: column.property,
                title: column.title,
                sortOrder: column.withinSubgroupOrder
              };
            })
            .filter(Boolean); // Filter out null values

          subgroupColumnList.push({
            key: subgroup,
            title: subgroup,
            columns: sortBy(columnsList, ["sortOrder"])
          });
        }
      }

      const groupObj: IGroupColumn = {
        key: group.name,
        title: group.name,
        hexColor: group.hexColor,
        productGroups: productGroupColumnList,
        subgroups: subgroupColumnList,
        sortOrder: group.sortOrder
      };
      return groupObj;
    });

  let globalOrder = 0;
  const map: IPropertyColumnMap = {};
  tree.forEach((group) => {
    group.subgroups.forEach((subgroup) => {
      subgroup.columns.forEach((column) => {
        column.globalOrder = globalOrder++;
        map[column.property] = column;
      });
    });
    group.productGroups.forEach((prodGroup) => {
      prodGroup.subgroups.forEach((subgroup) => {
        subgroup.columns.forEach((column) => {
          column.globalOrder = globalOrder++;
          map[column.property] = column;
        });
      });
    });
  });

  return [tree, map];
};

export default getColumnPropertyTree;
